import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import KanbanAPI from '../../api/kanban-board/kanban-api-factory';
import { FloatingTextInput } from '../reusable-elements/floating-text-input';
import { PhotoGallery } from '../reusable-elements/photo-gallery';
import { PhotoUploader } from '../reusable-elements/photo-uploader';
import { FileApi } from '../../api/file/file-api';
import { CompanyAPI } from '../../api/company/company-api';

export const TaskDetail = () => {
    const { propertyId, maintenanceId, inspectionId, taskId } = useParams();
    const itemId = maintenanceId || inspectionId || taskId;
    const navigate = useNavigate();
    const { instance, inProgress, accounts } = useMsal();
    const [taskItem, setTaskItem] = useState(null);
    const [originalItem, setOriginalItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [newLabel, setNewLabel] = useState('');
    const [editingLabel, setEditingLabel] = useState(null);
    const [editLabelText, setEditLabelText] = useState('');
    const [editingStatus, setEditingStatus] = useState(false);
    const [editingPriority, setEditingPriority] = useState(false);
    const [tempStatus, setTempStatus] = useState('');
    const [tempPriority, setTempPriority] = useState('');
    const [contractorSearch, setContractorSearch] = useState('');
    const [showContractorDropdown, setShowContractorDropdown] = useState(false);
    const [newSubTaskTitle, setNewSubTaskTitle] = useState('');
    const [showAddSubTask, setShowAddSubTask] = useState(false);
    const [templateData, setTemplateData] = useState(null);
    const [boardData, setBoardData] = useState(null);
    const [photosToDelete, setPhotosToDelete] = useState([]);
    const [saving, setSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, fileName: '' });
    const [requestingTokens, setRequestingTokens] = useState(false);
    const [duplicateFiles, setDuplicateFiles] = useState([]);
    const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

    const companyApi = useMemo(() => new CompanyAPI(instance, inProgress, accounts, true), [instance, inProgress, accounts]);
    const kanbanApi = useMemo(() => new KanbanAPI(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    const statusOptions = ['Open', 'In Progress', 'Closed'];
    const priorityOptions = ['Low', 'Medium', 'High', 'Critical'];

    // Mock contractor data
    const mockContractors = [
        { id: 1, name: 'John Smith Plumbing', specialty: 'Plumbing', phone: '01234 567890' },
        { id: 2, name: 'ABC Heating Services', specialty: 'Heating & Gas', phone: '01234 567891' },
        { id: 3, name: 'Quick Fix Electricians', specialty: 'Electrical', phone: '01234 567892' },
        { id: 4, name: 'Clean Gutters Ltd', specialty: 'Guttering', phone: '01234 567893' },
        { id: 5, name: 'Perfect Paint Co', specialty: 'Decorating', phone: '01234 567894' },
        { id: 6, name: 'Roof Masters', specialty: 'Roofing', phone: '01234 567895' },
        { id: 7, name: 'Green Thumb Gardens', specialty: 'Landscaping', phone: '01234 567896' },
        { id: 8, name: 'Secure Locks Ltd', specialty: 'Locksmith', phone: '01234 567897' }
    ];

    const filteredContractors = mockContractors.filter(contractor =>
        contractor.name.toLowerCase().includes(contractorSearch.toLowerCase()) ||
        contractor.specialty.toLowerCase().includes(contractorSearch.toLowerCase())
    );

    useEffect(() => {
        const loadTaskItem = async () => {
            try {
                // Get the task directly by ID
                const task = await kanbanApi.getSingleTileById(itemId);
                
                if (task) {
                    // Load board data for breadcrumbs and context
                    if (task.boardId) {
                        const board = await kanbanApi.getProject(task.boardId);
                        setBoardData(board);
                    }
                }

                if (task) {
                    setTaskItem(task);
                    setOriginalItem(JSON.parse(JSON.stringify(task))); // Deep copy for comparison
                }

                // Load template data to check if it's an inspection
                const templates = await kanbanApi.loadSystemTemplates();
                setTemplateData(templates);
            } catch (error) {
                console.error('Error loading task item:', error);
            } finally {
                setLoading(false);
            }
        };

        if (itemId) {
            loadTaskItem();
        } else {
            setLoading(false);
        }
    }, [propertyId, itemId, kanbanApi]);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    const getStatusBadge = (status) => {
        const classes = {
            'Open': 'badge bg-primary',
            'In Progress': 'badge bg-warning',
            'Closed': 'badge bg-success'
        };
        return classes[status] || 'badge bg-secondary';
    };

    const getPriorityBadge = (priority) => {
        const classes = {
            'Critical': 'badge bg-danger',
            'High': 'badge bg-warning text-dark',
            'Medium': 'badge bg-info',
            'Low': 'badge bg-secondary'
        };
        return classes[priority] || 'badge bg-secondary';
    };

    const addLabel = () => {
        if (newLabel.trim() && taskItem) {
            const updatedTags = [...(taskItem.tags || []), newLabel.trim()];
            const updatedItem = {
                ...taskItem,
                tags: updatedTags
            };
            setTaskItem(updatedItem);
            setNewLabel('');
        }
    };

    const removeLabel = (labelToRemove) => {
        if (taskItem) {
            const updatedTags = (taskItem.tags || []).filter(label => label !== labelToRemove);
            const updatedItem = {
                ...taskItem,
                tags: updatedTags
            };
            setTaskItem(updatedItem);
        }
    };

    const startEditLabel = (label) => {
        setEditingLabel(label);
        setEditLabelText(label);
    };

    const saveEditLabel = () => {
        if (editLabelText.trim() && taskItem && editingLabel) {
            const updatedTags = (taskItem.tags || []).map(label =>
                label === editingLabel ? editLabelText.trim() : label
            );
            const updatedItem = {
                ...taskItem,
                tags: updatedTags
            };
            setTaskItem(updatedItem);
            setEditingLabel(null);
            setEditLabelText('');
        }
    };

    const cancelEditLabel = () => {
        setEditingLabel(null);
        setEditLabelText('');
    };

    const updateStatus = (newStatus) => {
        if (taskItem && newStatus) {
            const updatedItem = { ...taskItem, status: newStatus };
            setTaskItem(updatedItem);
            setEditingStatus(false);
        }
    };

    const updatePriority = (newPriority) => {
        if (taskItem && newPriority) {
            const updatedItem = { ...taskItem, priority: newPriority };
            setTaskItem(updatedItem);
            setEditingPriority(false);
        }
    };

    const hasChanges = () => {
        if (!taskItem || !originalItem) return false;
        return (
            taskItem.name !== originalItem.name ||
            taskItem.description !== originalItem.description ||
            taskItem.status !== originalItem.status ||
            taskItem.priority !== originalItem.priority ||
            taskItem.assignedTo !== originalItem.assignedTo ||
            JSON.stringify(taskItem.tags || []) !== JSON.stringify(originalItem.tags || []) ||
            JSON.stringify(taskItem.subTasks || []) !== JSON.stringify(originalItem.subTasks || []) ||
            JSON.stringify(taskItem.photos || []) !== JSON.stringify(originalItem.photos || []) ||
            photosToDelete.length > 0
        );
    };

    const getImageDimensions = (file) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve({ width: img.width, height: img.height });
            img.onerror = () => resolve(null);
            img.src = URL.createObjectURL(file);
        });
    };

    const checkForDuplicates = () => {
        const duplicates = [];
        const existingFileNames = new Set();
        
        // Collect existing file names
        (taskItem.photos || []).filter(p => !p.isNew).forEach(photo => {
            existingFileNames.add(photo.title);
        });
        
        (taskItem.subTasks || []).forEach(subTask => {
            (subTask.photos || []).filter(p => !p.isNew).forEach(photo => {
                existingFileNames.add(photo.title);
            });
        });
        
        // Check new files against existing
        (taskItem.photos || []).filter(p => p.isNew).forEach(photo => {
            if (existingFileNames.has(photo.file.name)) {
                duplicates.push({ type: 'task', fileName: photo.file.name });
            }
        });
        
        (taskItem.subTasks || []).forEach((subTask, subTaskIndex) => {
            (subTask.photos || []).filter(p => p.isNew).forEach(photo => {
                if (existingFileNames.has(photo.file.name)) {
                    duplicates.push({ type: 'subtask', fileName: photo.file.name, subTaskTitle: subTask.title });
                }
            });
        });
        return duplicates;
    };

    const saveChanges = async (forceSave = false) => {
        if (!forceSave) {
            const duplicates = checkForDuplicates();
            if (duplicates.length > 0) {
                setDuplicateFiles(duplicates);
                setShowDuplicateWarning(true);
                return;
            }
        }
        
        setSaving(true);
        setUploadProgress({ current: 0, total: 0, fileName: '' });
        setRequestingTokens(false);
        
        try {
            const fileApi = new FileApi(instance, inProgress, accounts, false);
            const api = kanbanApi;

            // Get company name from CompanyAPI
            let companyName = null;
            if (boardData?.companyId) {
                try {
                    const company = await companyApi.get(boardData.companyId);
                    companyName = company?.name;
                } catch (error) {
                    console.error('Error fetching company:', error);
                }
            }

            if (!companyName) {
                throw new Error('Unable to determine company name for file upload');
            }

            const containerName = boardData?.blobContainerName;
            if (!!containerName) {
                const deleteFileModel = {
                    containerName: containerName,
                    deletedItems: photosToDelete.map(p => ({ path: p, isFolder: false }))
                };

                // Delete photos first
                await fileApi.queueFileDeletion(deleteFileModel);
            }

            // Upload new photos
            const updatedTaskItem = { ...taskItem };

            // Count total files to upload
            const newTaskPhotos = (taskItem.photos || []).filter(p => p.isNew);
            let totalSubTaskPhotos = 0;
            (updatedTaskItem.subTasks || []).forEach(subTask => {
                totalSubTaskPhotos += (subTask.photos || []).filter(p => p.isNew).length;
            });
            const totalFiles = newTaskPhotos.length + totalSubTaskPhotos;

            // Show token request indicator if there are files to upload
            if (totalFiles > 0) {
                setRequestingTokens(true);
            }

            let fileCounter = 0;
            const fileNames = [...newTaskPhotos.map(p => p.file.name)];
            (updatedTaskItem.subTasks || []).forEach(subTask => {
                const newSubTaskPhotos = (subTask.photos || []).filter(p => p.isNew);
                fileNames.push(...newSubTaskPhotos.map(p => p.file.name));
            });
            
            const createProgressCallback = () => (progressData) => {
                setRequestingTokens(false); // Hide token request indicator once upload starts
                const fileName = fileNames[fileCounter] || (typeof progressData === 'string' ? progressData : progressData?.name || 'Unknown file');
                fileCounter++;

                setUploadProgress({ current: fileCounter, total: totalFiles, fileName });
            };

            // Handle task photos
            if (newTaskPhotos.length > 0) {
                const taskPath = `${taskItem.boardId}/${taskItem.id}`;
                const uploadResponses = await fileApi.uploadFiles(companyName, taskPath, newTaskPhotos.map(p => p.file), createProgressCallback());
                setRequestingTokens(false); // Ensure token indicator is hidden after upload
                const uploadedPhotos = await Promise.all(uploadResponses.map(async r => {
                    const dimensions = await getImageDimensions(r.file);
                    return {
                        path: r.path,
                        title: r.file.name,
                        created: new Date().toISOString(),
                        dimensions: dimensions
                    };
                }));
                updatedTaskItem.photos = [...(taskItem.photos || []).filter(p => !p.isNew), ...uploadedPhotos];
            }

            // Handle subtask photos
            for (let i = 0; i < (updatedTaskItem.subTasks || []).length; i++) {
                const subTask = updatedTaskItem.subTasks[i];
                const newSubTaskPhotos = (subTask.photos || []).filter(p => p.isNew);
                if (newSubTaskPhotos.length > 0) {
                    const subTaskPath = `${taskItem.boardId}/${taskItem.id}/${subTask.subTaskId}`;
                    const uploadResponses = await fileApi.uploadFiles(companyName, subTaskPath, newSubTaskPhotos.map(p => p.file), createProgressCallback());
                    setRequestingTokens(false); // Ensure token indicator is hidden after upload
                    const uploadedPhotos = await Promise.all(uploadResponses.map(async r => {
                        const dimensions = await getImageDimensions(r.file);
                        return {
                            path: r.path,
                            title: r.file.name,
                            created: new Date().toISOString(),
                            dimensions: dimensions
                        };
                    }));
                    updatedTaskItem.subTasks[i].photos = [...(subTask.photos || []).filter(p => !p.isNew), ...uploadedPhotos];
                }
            }

            const result = await api.upsertTile(updatedTaskItem);
            const updatedTask = result.columns.flatMap(col => col.items).find(tile => tile.id === taskItem.id);
            setTaskItem(updatedTask || updatedTaskItem);
            setOriginalItem(JSON.parse(JSON.stringify(updatedTask || updatedTaskItem)));
            setPhotosToDelete([]);
        } catch (error) {
            console.error('Error saving changes:', error);
        } finally {
            setSaving(false);
            setUploadProgress({ current: 0, total: 0, fileName: '' });
            setRequestingTokens(false);
        }
    };

    const cancelChanges = () => {
        setTaskItem(JSON.parse(JSON.stringify(originalItem)));
        setEditingStatus(false);
        setEditingPriority(false);
        setEditingLabel(null);
        setNewLabel('');
        setShowAddSubTask(false);
        setNewSubTaskTitle('');
    };

    const addSubTask = () => {
        if (newSubTaskTitle.trim() && taskItem) {
            const newSubTask = {
                subTaskId: Date.now().toString(),
                title: newSubTaskTitle.trim(),
                isCompleted: false,
                order: (taskItem.subTasks || []).length
            };

            // Add checkCompleteStatus for inspection tasks
            if (taskItem.taskType === 'Inspection') {
                newSubTask.checkCompleteStatus = 'na';
            }

            const updatedSubTasks = [...(taskItem.subTasks || []), newSubTask];
            setTaskItem({ ...taskItem, subTasks: updatedSubTasks });
            setNewSubTaskTitle('');
            setShowAddSubTask(false);
        }
    };

    const removeSubTask = (index) => {
        if (taskItem) {
            const updatedSubTasks = [...taskItem.subTasks];
            updatedSubTasks.splice(index, 1);
            setTaskItem({ ...taskItem, subTasks: updatedSubTasks });
        }
    };

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="text-center p-4">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!taskItem) {
        return (
            <div className="container-fluid">
                <div className="alert alert-warning">
                    Task not found.
                    <button className="btn btn-link" onClick={() => navigate(-1)}>Go Back</button>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb mb-0">
                                {boardData && (
                                    <li className="breadcrumb-item">
                                        <a href={`#/kanban/${boardData.id}`} className="text-primary">{boardData.name}</a>
                                    </li>
                                )}
                                {boardData?.property?.address && (
                                    <li className="breadcrumb-item">
                                        <span className="text-muted">{boardData.property.address.split(',')[0]}</span>
                                    </li>
                                )}
                                <li className="breadcrumb-item active" aria-current="page">
                                    {taskItem.name}
                                </li>
                            </ol>
                        </nav>
                        <div className="d-flex gap-2 flex-column">
                            <div className="d-flex gap-2">
                                {hasChanges() && (
                                    <>
                                        <button className="btn btn-success" onClick={() => saveChanges()} disabled={saving}>
                                            {saving ? (
                                                requestingTokens ? (
                                                    'Requesting upload tokens...'
                                                ) : uploadProgress.total > 0 ? (
                                                    `Uploading ${uploadProgress.current}/${uploadProgress.total} files...`
                                                ) : 'Saving...'
                                            ) : 'Save Changes'}
                                        </button>
                                        <button className="btn btn-outline-secondary" onClick={cancelChanges}>
                                            Cancel
                                        </button>
                                    </>
                                )}
                                <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                                    <i className="fa fa-arrow-left"></i> Back
                                </button>
                            </div>
                            {saving && (requestingTokens || uploadProgress.total > 0) && (
                                <div className="w-100">
                                    {requestingTokens ? (
                                        <>
                                            <div className="progress" style={{ height: '20px' }}>
                                                <div 
                                                    className="progress-bar progress-bar-striped progress-bar-animated" 
                                                    role="progressbar" 
                                                    style={{ width: '100%' }}
                                                >
                                                    Requesting upload tokens...
                                                </div>
                                            </div>
                                            <small className="text-muted">Preparing files for upload</small>
                                        </>
                                    ) : (
                                        <>
                                            <div className="progress" style={{ height: '20px' }}>
                                                <div 
                                                    className="progress-bar" 
                                                    role="progressbar" 
                                                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                                                    aria-valuenow={uploadProgress.current}
                                                    aria-valuemin="0"
                                                    aria-valuemax={uploadProgress.total}
                                                >
                                                    {uploadProgress.current}/{uploadProgress.total}
                                                </div>
                                            </div>
                                            <small className="text-muted">Uploading: {uploadProgress.fileName}</small>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-8">
                                    <div className="mb-3">
                                        <FloatingTextInput
                                            label="Title"
                                            text={taskItem.name}
                                            updateCallback={(value) => setTaskItem({ ...taskItem, name: value })}
                                            required={true}
                                            validatorFn={() => true}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <FloatingTextInput
                                            label="Description"
                                            text={taskItem.description}
                                            updateCallback={(value) => setTaskItem({ ...taskItem, description: value })}
                                            required={false}
                                            validatorFn={() => true}
                                        />
                                    </div>

                                    <div className="mb-3 position-relative">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={contractorSearch || taskItem.assignedTo || ''}
                                                onChange={(e) => {
                                                    setContractorSearch(e.target.value);
                                                    setShowContractorDropdown(true);
                                                }}
                                                onFocus={() => setShowContractorDropdown(true)}
                                                placeholder="Search contractors..."
                                            />
                                            <label>Assigned To</label>
                                        </div>
                                        {showContractorDropdown && (
                                            <div className="dropdown-menu show position-absolute w-100" style={{ zIndex: 1050 }}>
                                                {filteredContractors.length > 0 ? (
                                                    filteredContractors.map(contractor => (
                                                        <button
                                                            key={contractor.id}
                                                            className="dropdown-item d-flex justify-content-between"
                                                            onClick={() => {
                                                                setTaskItem({ ...taskItem, assignedTo: contractor.name });
                                                                setContractorSearch('');
                                                                setShowContractorDropdown(false);
                                                            }}
                                                        >
                                                            <div>
                                                                <div className="fw-bold">{contractor.name}</div>
                                                                <small className="text-muted">{contractor.specialty}</small>
                                                            </div>
                                                            <small className="text-muted">{contractor.phone}</small>
                                                        </button>
                                                    ))
                                                ) : (
                                                    <div className="dropdown-item-text">No contractors found</div>
                                                )}
                                                <div className="dropdown-divider"></div>
                                                <button
                                                    className="dropdown-item text-primary"
                                                    onClick={() => {
                                                        setShowContractorDropdown(false);
                                                        // TODO: Implement add new contractor functionality
                                                    }}
                                                >
                                                    + Add New Contractor
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <h5>Created Date</h5>
                                        <p>{formatDate(taskItem.created)}</p>
                                    </div>

                                    {taskItem.completed && (
                                        <div className="mb-3">
                                            <h5>Completed Date</h5>
                                            <p>{formatDate(taskItem.completed)}</p>
                                        </div>
                                    )}

                                    <div className="mb-3">
                                        <h5>Photos</h5>
                                        <PhotoGallery
                                            photos={taskItem.photos || []}
                                            onRemovePhoto={(index) => {
                                                const photoToRemove = taskItem.photos[index];
                                                if (!photoToRemove.isNew && photoToRemove.path) {
                                                    setPhotosToDelete(prev => [...prev, photoToRemove.path]);
                                                }
                                                const updatedPhotos = taskItem.photos.filter((_, i) => i !== index);
                                                setTaskItem({ ...taskItem, photos: updatedPhotos });
                                            }}
                                        />
                                        <PhotoUploader
                                            onPhotosAdded={(newPhoto) => {
                                                setTaskItem(prev => ({
                                                    ...prev,
                                                    photos: [...(prev.photos || []), newPhoto]
                                                }));
                                            }}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h5>{taskItem.taskType === 'Inspection' ? 'Inspection Items' : 'Sub Tasks'}</h5>
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => setShowAddSubTask(true)}
                                            >
                                                + Add Sub Task
                                            </button>
                                        </div>
                                        {showAddSubTask && (
                                            <div className="mb-2">
                                                <div className="d-flex gap-2 align-items-end">
                                                    <div className="flex-grow-1">
                                                        <FloatingTextInput
                                                            label="Sub Task Title"
                                                            text={newSubTaskTitle}
                                                            updateCallback={(value) => setNewSubTaskTitle(value)}
                                                            required={true}
                                                            validatorFn={(text) => text.trim().length > 0}
                                                        />
                                                    </div>
                                                    <button
                                                        className="btn btn-success"
                                                        onClick={addSubTask}
                                                        disabled={!newSubTaskTitle.trim()}
                                                    >
                                                        Add
                                                    </button>
                                                    <button
                                                        className="btn btn-secondary"
                                                        onClick={() => {
                                                            setShowAddSubTask(false);
                                                            setNewSubTaskTitle('');
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        {(taskItem.subTasks || []).length > 0 ? (
                                            <div className="list-group">
                                                {taskItem.subTasks.map((subTask, index) => (
                                                    <div key={subTask.subTaskId || index} className="list-group-item">
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <div className="d-flex align-items-center flex-grow-1">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input me-3"
                                                                    checked={subTask.isCompleted || false}
                                                                    onChange={(e) => {
                                                                        const updatedSubTasks = [...taskItem.subTasks];
                                                                        updatedSubTasks[index] = { ...subTask, isCompleted: e.target.checked };
                                                                        setTaskItem({ ...taskItem, subTasks: updatedSubTasks });
                                                                    }}
                                                                />
                                                                <span className={subTask.isCompleted ? 'text-muted' : ''}>
                                                                    {subTask.title}
                                                                </span>
                                                            </div>
                                                            <div className="d-flex align-items-center gap-2">
                                                                {(taskItem.taskType === 'Inspection' || (templateData?.items?.find(t => t.id === taskItem.templateId)?.isInspection)) && (
                                                                    <div
                                                                        style={{ cursor: 'pointer' }}
                                                                        onClick={() => {
                                                                            const updatedSubTasks = [...taskItem.subTasks];
                                                                            const currentStatus = subTask.checkCompleteStatus || 'na';
                                                                            let nextStatus;
                                                                            switch (currentStatus) {
                                                                                case 'na':
                                                                                    nextStatus = 'passed';
                                                                                    break;
                                                                                case 'passed':
                                                                                    nextStatus = 'failed';
                                                                                    break;
                                                                                case 'failed':
                                                                                    nextStatus = 'na';
                                                                                    break;
                                                                                default:
                                                                                    nextStatus = 'na';
                                                                            }
                                                                            updatedSubTasks[index] = { ...subTask, checkCompleteStatus: nextStatus };
                                                                            setTaskItem({ ...taskItem, subTasks: updatedSubTasks });
                                                                        }}
                                                                    >
                                                                        {(!subTask.checkCompleteStatus || subTask.checkCompleteStatus === 'na') && (
                                                                            <div className="badge bg-default" style={{ width: '31px', height: '31px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                                <i className="fas fa-xl fa-fw fa-expand text-black-900 fw-600" title="Not Checked or N/A" />
                                                                            </div>
                                                                        )}
                                                                        {subTask.checkCompleteStatus === 'passed' && (
                                                                            <div className="badge bg-success" style={{ width: '31px', height: '31px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                                <i className="fas fa-xl fa-fw fa-check fw-600" title="Passed" />
                                                                            </div>
                                                                        )}
                                                                        {subTask.checkCompleteStatus === 'failed' && (
                                                                            <div className="badge bg-danger" style={{ width: '31px', height: '31px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                                <i className="fas fa-xl fa-fw fa-x fw-600" title="Failed" />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                <PhotoUploader
                                                                    showAsButton={true}
                                                                    buttonText="Add photos"
                                                                    onPhotosAdded={(newPhoto) => {
                                                                        const updatedSubTasks = [...taskItem.subTasks];
                                                                        updatedSubTasks[index] = {
                                                                            ...subTask,
                                                                            photos: [...(subTask.photos || []), newPhoto]
                                                                        };
                                                                        setTaskItem({ ...taskItem, subTasks: updatedSubTasks });
                                                                    }}
                                                                />
                                                                <button
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    onClick={() => removeSubTask(index)}
                                                                    title={`Remove ${taskItem.taskType === 'Inspection' ? 'inspection item' : 'sub task'}`}
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {(subTask.photos || []).length > 0 && (
                                                            <div className="mt-3 ps-5">
                                                                <PhotoGallery
                                                                    photos={subTask.photos}
                                                                    onRemovePhoto={(photoIndex) => {
                                                                        const photoToRemove = subTask.photos[photoIndex];
                                                                        if (!photoToRemove.isNew && photoToRemove.Path) {
                                                                            setPhotosToDelete(prev => [...prev, photoToRemove.Path]);
                                                                        }
                                                                        const updatedSubTasks = [...taskItem.subTasks];
                                                                        const updatedPhotos = subTask.photos.filter((_, i) => i !== photoIndex);
                                                                        updatedSubTasks[index] = { ...subTask, photos: updatedPhotos };
                                                                        setTaskItem({ ...taskItem, subTasks: updatedSubTasks });
                                                                    }}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-muted">No {taskItem.taskType === 'Inspection' ? 'inspection items' : 'sub tasks'}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="mb-3">
                                        <h6>Status</h6>
                                        {editingStatus ? (
                                            <div className="d-flex gap-2">
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={tempStatus}
                                                    onChange={(e) => setTempStatus(e.target.value)}
                                                    style={{ width: '150px' }}
                                                >
                                                    {statusOptions.map(option => (
                                                        <option key={option} value={option}>{option}</option>
                                                    ))}
                                                </select>
                                                <button
                                                    className="btn btn-sm btn-success"
                                                    onClick={() => updateStatus(tempStatus)}
                                                >
                                                    ✓
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-secondary"
                                                    onClick={() => setEditingStatus(false)}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ) : (
                                            <span
                                                className={`${getStatusBadge(taskItem.status || 'Open')} cursor-pointer`}
                                                onClick={() => {
                                                    setEditingStatus(true);
                                                    setTempStatus(taskItem.status || 'Open');
                                                }}
                                                title="Click to edit"
                                                style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                                            >
                                                {taskItem.status || 'Open'}
                                            </span>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <h6>Priority</h6>
                                        {editingPriority ? (
                                            <div className="d-flex gap-2">
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={tempPriority}
                                                    onChange={(e) => setTempPriority(e.target.value)}
                                                    style={{ width: '150px' }}
                                                >
                                                    {priorityOptions.map(option => (
                                                        <option key={option} value={option}>{option}</option>
                                                    ))}
                                                </select>
                                                <button
                                                    className="btn btn-sm btn-success"
                                                    onClick={() => updatePriority(tempPriority)}
                                                >
                                                    ✓
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-secondary"
                                                    onClick={() => setEditingPriority(false)}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ) : (
                                            <span
                                                className={`${getPriorityBadge(taskItem.priority || 'Medium')} cursor-pointer`}
                                                onClick={() => {
                                                    setEditingPriority(true);
                                                    setTempPriority(taskItem.priority || 'Medium');
                                                }}
                                                title="Click to edit"
                                                style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                                            >
                                                {taskItem.priority || 'Medium'}
                                            </span>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <h6>Labels</h6>
                                        <div className="mb-2">
                                            {(taskItem.tags || []).map((label, index) => (
                                                <span key={index} className="badge bg-secondary me-2 mb-1 position-relative" style={{ fontSize: '0.9rem' }}>
                                                    {editingLabel === label ? (
                                                        <div className="d-inline-flex align-items-center">
                                                            <input
                                                                type="text"
                                                                value={editLabelText}
                                                                onChange={(e) => setEditLabelText(e.target.value)}
                                                                className="form-control form-control-sm me-1"
                                                                style={{ width: '100px', display: 'inline-block' }}
                                                                onKeyPress={(e) => e.key === 'Enter' && saveEditLabel()}
                                                                autoFocus
                                                            />
                                                            <button
                                                                className="btn btn-sm btn-success me-1"
                                                                onClick={saveEditLabel}
                                                                style={{ padding: '2px 6px' }}
                                                            >
                                                                ✓
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-secondary"
                                                                onClick={cancelEditLabel}
                                                                style={{ padding: '2px 6px' }}
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <span
                                                                onClick={() => startEditLabel(label)}
                                                                style={{ cursor: 'pointer' }}
                                                                title="Click to edit"
                                                            >
                                                                {label}
                                                            </span>
                                                            <button
                                                                className="btn btn-sm ms-1"
                                                                onClick={() => removeLabel(label)}
                                                                style={{ padding: '0', border: 'none', background: 'none', color: 'white', fontSize: '12px' }}
                                                                title="Remove label"
                                                            >
                                                                ✕
                                                            </button>
                                                        </>
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                        <div className="input-group input-group-sm" style={{ maxWidth: '250px' }}>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Add new label"
                                                value={newLabel}
                                                onChange={(e) => setNewLabel(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && addLabel()}
                                            />
                                            <button
                                                className="btn btn-outline-primary"
                                                onClick={addLabel}
                                                disabled={!newLabel.trim()}
                                            >
                                                Add
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Duplicate Files Warning Modal */}
            {showDuplicateWarning && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Duplicate Files Detected</h5>
                            </div>
                            <div className="modal-body">
                                <p>The following files have the same names as existing files:</p>
                                <ul>
                                    {duplicateFiles.map((dup, index) => (
                                        <li key={index}>
                                            <strong>{dup.fileName}</strong>
                                            {dup.type === 'subtask' && ` (in ${dup.subTaskTitle})`}
                                        </li>
                                    ))}
                                </ul>
                                <p>Do you want to continue? This will overwrite the existing files.</p>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowDuplicateWarning(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-warning" 
                                    onClick={() => {
                                        setShowDuplicateWarning(false);
                                        saveChanges(true);
                                    }}
                                >
                                    Continue & Overwrite
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};